import React, { useState, useEffect } from "react";
import * as Popover from "@radix-ui/react-popover";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, ChevronDown, Check } from "lucide-react";
import Calendar from "react-calendar";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";
import {
  getPresetPeriods,
  calculatePeriodDates,
  formatDateDisplay,
  formatDateShort,
  isValidDateRange,
  getPresetLabel,
  isPresetPeriod,
} from "./periodUtils";
import "./calendarStyles.css";

/**
 * PeriodPicker - A dropdown component for selecting date periods
 *
 * @param {Object} value - Current period value { startDate, endDate, periodId, label }
 * @param {Function} onChange - Callback when period changes
 * @param {string} align - Dropdown alignment: "left" | "right" (default: "right")
 */
const PeriodPicker = ({ value, onChange, align = "right" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);

  const presets = getPresetPeriods();

  // Reset calendar state when closing
  useEffect(() => {
    if (!open) {
      setShowCalendar(false);
      // Reset date range to current value if custom
      if (value?.periodId === "custom" && value.startDate && value.endDate) {
        setDateRange([value.startDate, value.endDate]);
      }
    }
  }, [open, value]);

  const handlePresetSelect = (preset) => {
    setShowCalendar(false);
    const dates = calculatePeriodDates(preset.id);
    onChange({ ...dates, periodId: preset.id, label: preset.label });
    setOpen(false);
  };

  const handleCustomClick = () => {
    setShowCalendar(true);
  };

  const handleCalendarChange = (range) => {
    if (Array.isArray(range)) {
      setDateRange(range);
    }
  };

  const handleCustomApply = () => {
    const [start, end] = dateRange;
    if (start && end && isValidDateRange(start, end)) {
      onChange({
        startDate: start,
        endDate: end,
        periodId: "custom",
        label: t("navigation.periodPicker.customRangeLabel", {
          start: formatDateShort(start),
          end: formatDateShort(end),
        }),
      });
      setOpen(false);
    }
  };

  const selectedPeriodId = value?.periodId || "30d";
  // Presets are re-translated on render so the label follows the active
  // language; custom ranges keep the label built when they were applied.
  const displayLabel = isPresetPeriod(selectedPeriodId)
    ? getPresetLabel(selectedPeriodId)
    : value?.label || getPresetLabel("30d");
  const isCustomSelected = selectedPeriodId === "custom";
  const canApplyCustom =
    dateRange[0] &&
    dateRange[1] &&
    isValidDateRange(dateRange[0], dateRange[1]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-sm text-[#374151] transition duration-150 hover:bg-[#FAFAFA]">
          <CalendarIcon size={14} className="text-[#6B7280]" />
          <span>{displayLabel}</span>
          <ChevronDown
            size={14}
            className={`text-[#9CA3AF] transition-transform duration-150 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </Popover.Trigger>

      <AnimatePresence>
        {open && (
          <Popover.Portal forceMount>
            <Popover.Content
              align={align === "left" ? "start" : "end"}
              sideOffset={8}
              collisionPadding={16}
              asChild
            >
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="z-[200] overflow-hidden rounded-lg border border-[#F3F4F6] bg-white shadow-lg"
                style={{ minWidth: showCalendar ? "240px" : "160px" }}
              >
                {/* Preset periods */}
                <div className="py-0.5">
                  {presets.map((preset) => {
                    const isSelected = selectedPeriodId === preset.id;
                    return (
                      <button
                        key={preset.id}
                        onClick={() => handlePresetSelect(preset)}
                        className={`flex w-full items-center justify-between px-2.5 py-1.5 text-xs transition duration-150 ${
                          isSelected
                            ? "bg-[#FEF3E7] text-[#E07A3A]"
                            : "text-[#374151] hover:bg-[#FAFAFA]"
                        }`}
                      >
                        <span>{preset.label}</span>
                        {isSelected && <Check size={12} />}
                      </button>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="border-t border-[#F3F4F6]" />

                {/* Custom range option */}
                <div className="py-0.5">
                  <button
                    onClick={handleCustomClick}
                    className={`flex w-full items-center justify-between px-2.5 py-1.5 text-xs transition duration-150 ${
                      isCustomSelected || showCalendar
                        ? "bg-[#FEF3E7] text-[#E07A3A]"
                        : "text-[#374151] hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <span>{t("navigation.periodPicker.customRange")}</span>
                    {isCustomSelected && !showCalendar && <Check size={12} />}
                  </button>
                </div>

                {/* Calendar for custom range */}
                <AnimatePresence>
                  {showCalendar && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-[#F3F4F6] p-2">
                        <Calendar
                          className="period-picker-calendar"
                          selectRange={true}
                          locale={dateLocale(undefined)}
                          value={dateRange}
                          onChange={handleCalendarChange}
                          maxDate={new Date()}
                          showDoubleView={false}
                          prev2Label={null}
                          next2Label={null}
                        />

                        {/* Selected range display */}
                        {dateRange[0] && dateRange[1] && (
                          <div className="mt-2 border-t border-[#F3F4F6] pt-2">
                            <div className="mb-2 flex items-center justify-between text-[10px] text-[#6B7280]">
                              <span>{formatDateDisplay(dateRange[0])}</span>
                              <span className="text-[#9CA3AF]">
                                {t("navigation.periodPicker.rangeSeparator")}
                              </span>
                              <span>{formatDateDisplay(dateRange[1])}</span>
                            </div>
                            <button
                              onClick={handleCustomApply}
                              disabled={!canApplyCustom}
                              className={`w-full rounded-md py-1.5 text-xs font-medium transition duration-150 ${
                                canApplyCustom
                                  ? "bg-[#E07A3A] text-white hover:bg-[#D66B2E]"
                                  : "cursor-not-allowed bg-[#F3F4F6] text-[#9CA3AF]"
                              }`}
                            >
                              {t("common.actions.apply")}
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
};

export default PeriodPicker;
